/**
 * Document / transcript analysis from the chat: pick a file, choose options
 * in AnalysisModal, dispatch the analysis, and drive the AnalysisProgress
 * modal (complete, error, retry, cancel, view result).
 *
 * The handlers are plain functions rebuilt every render on purpose: they
 * close over the latest `pendingFile` and form state. A memoized
 * `handleAnalysisModalSubmit` once captured a stale `pendingFile` and
 * silently skipped the analysis.
 */
import { useRef, useState } from 'react';
import { Platform } from 'react-native';
import * as DocumentPicker from 'expo-document-picker';

import { showDialog } from '@/utils/dialogs';
import { trackAuthEvent } from '../../services/analytics';
import { documentsAPI, taskAPI, transcriptionAPI } from '../../services/api';
import { useIdempotencyIntent } from '../../services/idempotencyIntent';
import { openWebFilePicker } from '../../services/webFilePicker';
import type { AnalysisMode, AnalysisOptions, ChatMessage, ICFLevel, ToolResult } from '../../types';
import { trialAnalysisUsedMessage } from '../../utils/trialAnalysisUsed';
import { getAnalysisFileMimeType, getFileExtension } from './analysisFile';
import { WAITING_BUBBLE_ID, type PendingTask } from './chatConstants';
import { chatSendErrorMessage, errorStatusOf } from './chatErrors';
import type {
  LoadMessages,
  LoadSessions,
  Loader,
  SessionSwitching,
  SetMessages,
  SetState,
  TaskCompletion,
  TFn,
  TrialState,
} from './chatFlowTypes';
import type { ChatOperationRefs } from './useChatOperationRefs';

const isWebFileObject = (file: any): file is File => (
  Platform.OS === ('web' as any) &&
  typeof File !== 'undefined' &&
  file instanceof File
);

const getAnalysisUploadFile = (file: any): File | { uri: string; type: string; name: string } => {
  if (isWebFileObject(file)) {
    return file;
  }

  return {
    uri: file.uri,
    type: getAnalysisFileMimeType(file),
    name: file.name || 'document',
  };
};

export function useDocumentAnalysis({
  ops,
  language,
  t,
  waitingTipKey,
  currentSessionId,
  canIncludeInIcfProgress,
  analyzingFile,
  setAnalyzingFile,
  activeTaskId,
  setActiveTaskId,
  setMessages,
  showSession,
  adoptSession,
  addOptimisticMessage,
  addPendingTask,
  removePendingTask,
  loadMessages,
  loadSessions,
  loadQuotaStatus,
  loadRecentDocuments,
  waitForTask,
  trialStatus,
  refreshTrialStatus,
  refreshStatus,
  checkCredits,
  showTip,
  transcriptBlocked,
  showTranscriptLimitWarning,
  reportDocumentPickCancelled,
  handleAnalysisMilestone,
  markSessionAnalyzed,
  viewingTranscript,
  setViewingTranscript,
  setShowTranscriptViewer,
}: SessionSwitching & {
  ops: ChatOperationRefs;
  language: string;
  t: TFn;
  waitingTipKey: string;
  currentSessionId: number | null;
  canIncludeInIcfProgress: boolean;
  analyzingFile: boolean;
  setAnalyzingFile: SetState<boolean>;
  activeTaskId: string | null;
  setActiveTaskId: SetState<string | null>;
  setMessages: SetMessages;
  addOptimisticMessage: (sessionId: number, message: ChatMessage) => void;
  addPendingTask: (task: PendingTask) => void;
  removePendingTask: (taskId: string) => void;
  loadMessages: LoadMessages;
  loadSessions: LoadSessions;
  loadQuotaStatus: Loader;
  loadRecentDocuments: Loader;
  waitForTask: TaskCompletion['waitForTask'];
  trialStatus: TrialState['trialStatus'];
  refreshTrialStatus: TrialState['refreshTrialStatus'];
  refreshStatus: () => Promise<void>;
  checkCredits: () => boolean;
  showTip: (tipId: string) => void;
  transcriptBlocked: boolean;
  showTranscriptLimitWarning: () => void;
  reportDocumentPickCancelled: () => Promise<void>;
  handleAnalysisMilestone: (via: string) => Promise<void>;
  markSessionAnalyzed: (sessionId: number | null | undefined) => void;
  viewingTranscript: any;
  setViewingTranscript: SetState<any>;
  setShowTranscriptViewer: SetState<boolean>;
}) {
  const {
    activeOperationSessionRef,
    cancelledTaskIdsRef,
    isAnalyzingInProgressRef,
    messageCountRef,
    optimisticMessagesRef,
    pendingScrollToBottomRef,
    pendingToolIntentRef,
    pendingToolResultsRef,
    pendingUploadToolRef,
    shouldAutoScrollRef,
    suppressBubbleRef,
    taskWaiterRef,
  } = ops;

  const [showInstructionsModal, setShowInstructionsModal] = useState(false);

  // Analysis form states
  const [pendingFile, setPendingFile] = useState<any>(null);
  const [analysisMode, setAnalysisMode] = useState<AnalysisMode>('icf');
  const [requestedLevel, setRequestedLevel] = useState<ICFLevel>('auto');
  const [includeMarkerTable, setIncludeMarkerTable] = useState(false);
  const [customPrompt, setCustomPrompt] = useState('');
  const [analysisLanguage, setAnalysisLanguage] = useState<'tr' | 'en'>(language === 'en' ? 'en' : 'tr');
  const [includeInICFProgress, setIncludeInICFProgress] = useState(true);

  const cancelPendingAnalysisRef = useRef(false);

  // Analysis progress tracking (for transcript analysis with clear UX feedback)
  const [showAnalysisProgress, setShowAnalysisProgress] = useState(false);
  const [analysisTaskId, setAnalysisTaskId] = useState<string | null>(null);
  const [analysisSessionId, setAnalysisSessionId] = useState<number | null>(null);
  const [analyzingDocumentName, setAnalyzingDocumentName] = useState<string | null>(null);

  const analysisStartRef = useRef<number | null>(null);
  // Guards the auto-open-on-complete handler against the AnalysisProgress
  // modal's WebSocket + polling completion paths both firing for one task.
  const analysisCompletionHandledRef = useRef(false);
  // True only when the AnalysisProgress modal is driven by a session/document
  // upload (analyzeWithInstructions). The audio→analysis flow manages its own
  // navigation/close inline, so it leaves this false to avoid double handling.
  const analysisAutoOpenRef = useRef(false);
  // ASYNC-6: one Idempotency-Key per user intent. A double submit of the same
  // input reuses it (the backend replays the existing job); "Try again" after
  // a failure mints a new one, since the old key would return the failed job.
  const analysisIntent = useIdempotencyIntent();
  /** Re-dispatches the upload-driven analysis shown in AnalysisProgress. */
  const analysisRetryRef = useRef<
    ((idempotencyKey: string) => Promise<{ task_id: string; session_id?: number | null }>) | null
  >(null);
  const analysisRetryInFlightRef = useRef(false);
  // Only the upload-driven (analyzeWithInstructions) path offers "Try again";
  // the audio flow closes its modals on failure itself.
  const [analysisRetryAvailable, setAnalysisRetryAvailable] = useState(false);

  const openAnalysisModalForFile = (file: any) => {
    const fileMeta = {
      file_type: getAnalysisFileMimeType(file),
      file_ext: getFileExtension(file.name),
      file_size: file.size,
    };
    trackAuthEvent('document_attached', {
      source: 'chat',
      ...fileMeta,
    });

    trackAuthEvent('analysis_modal_opened', {
      source: 'chat',
      ...fileMeta,
    });
    setPendingFile(file);
    setAnalysisMode('icf');
    setRequestedLevel('auto');
    setIncludeMarkerTable(false);
    setCustomPrompt('');
    setAnalysisLanguage(language === 'en' ? 'en' : 'tr');
    setShowInstructionsModal(true);
  };

  const openWebAnalysisFilePicker = () => openWebFilePicker({
    accept: [
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'text/plain',
      '.pdf',
      '.docx',
      '.txt',
    ],
    onFile: openAnalysisModalForFile,
    onCancel: () => { void reportDocumentPickCancelled(); },
  });

  const pickDocumentForAnalysis = async () => {
    trackAuthEvent('chat_document_attach_clicked', { source: 'header_button' });

    // Show contextual tip on first attach
    showTip('tip_first_attach');

    // Prevent picking document if analysis is already in progress
    if (isAnalyzingInProgressRef.current) {
      console.log('🚫 pickDocumentForAnalysis blocked - analysis already in progress');
      return;
    }

    // Credit check - show blocker if no credits available
    if (!checkCredits()) {
      console.log('🚫 pickDocumentForAnalysis blocked - no credits available');
      return;
    }

    if (transcriptBlocked) {
      showTranscriptLimitWarning();
      return;
    }

    if (Platform.OS === ('web' as any) && openWebAnalysisFilePicker()) {
      return;
    }

    try {
      const result = await DocumentPicker.getDocumentAsync({
        type: ['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'text/plain'],
        copyToCacheDirectory: true,
      });

      if (result.canceled) {
        await reportDocumentPickCancelled();
        return;
      }

      if (result.assets && result.assets.length > 0) {
        openAnalysisModalForFile(result.assets[0]);
      }
    } catch {
      showDialog(t('chat.filePickError'));
    }
  };

  const analyzeWithInstructions = async (overrideOptions?: AnalysisOptions) => {
    if (!pendingFile) {
      trackAuthEvent('document_analysis_failed', {
        error_type: 'missing_file',
      });
      return;
    }

    // Resolve effective options — caller (e.g. AnalysisModal submit) can pass
    // them explicitly; otherwise fall back to the chat's local state. Captured
    // up-front so React's batched setState during the async flow can't shift them
    // out from under us.
    const effectiveAnalysisMode: AnalysisMode = overrideOptions?.analysisMode ?? analysisMode;
    const effectiveRequestedLevel: ICFLevel = overrideOptions?.requestedLevel ?? requestedLevel;
    const effectiveIncludeMarkerTable: boolean = overrideOptions?.includeMarkerTable ?? includeMarkerTable;
    const effectiveCustomPrompt: string = (overrideOptions?.customPrompt ?? customPrompt) || '';
    const overrideLanguage = overrideOptions?.language;
    const effectiveLanguage: 'tr' | 'en' =
      overrideLanguage === 'en' || overrideLanguage === 'tr'
        ? overrideLanguage
        : analysisLanguage;
    const effectiveIncludeInIcfProgress: boolean = canIncludeInIcfProgress
      && (overrideOptions?.includeInIcfProgress ?? includeInICFProgress);

    // For custom mode, require a prompt
    if (effectiveAnalysisMode === 'custom' && !effectiveCustomPrompt.trim()) {
      showDialog(t('chat.analysisInstructionsRequired'));
      return;
    }

    const isTranscriptAnalysis =
      Boolean(pendingFile?.isTranscript) ||
      pendingFile?.mimeType === 'text/transcript' ||
      (typeof pendingFile?.uri === 'string' && pendingFile.uri.startsWith('transcript://'));

    // Route through the full-screen AnalysisProgress modal for transcripts AND
    // manual "Upload session" document uploads. Only AI tool-driven uploads
    // (pendingUploadToolRef set) stay inline, since their result must flow back
    // into the ongoing chat conversation as a tool result.
    const routeToProgressModal = isTranscriptAnalysis || !pendingUploadToolRef.current;

    // CRITICAL: Synchronous guard to prevent double-execution
    // React state updates are async, so checking analyzingFile isn't enough
    if (isAnalyzingInProgressRef.current) {
      console.log('🚫 Analysis already in progress (ref guard), skipping duplicate call');
      return;
    }
    isAnalyzingInProgressRef.current = true;
    console.log('🔒 Analysis ref guard set to true');
    // Same picked file => same key, so a replayed submit never starts a 2nd job.
    const idempotencyKey = analysisIntent.key(pendingFile);

    let pendingTaskId: string | null = null;
    let operationSessionId: number | null = currentSessionId ?? null;
    const fileMeta = {
      file_type: getAnalysisFileMimeType(pendingFile),
      file_ext: getFileExtension(pendingFile?.name),
      file_size: pendingFile?.size,
      is_transcript: isTranscriptAnalysis,
    };

    // Build description string for user message display
    const analysisDescription = effectiveAnalysisMode === 'custom'
      ? effectiveCustomPrompt.trim()
      : `ICF ${effectiveRequestedLevel === 'auto' ? 'Auto-detect' : effectiveRequestedLevel.toUpperCase()} Assessment`;

    analysisStartRef.current = Date.now();
    trackAuthEvent('document_analysis_started', {
      ...fileMeta,
      session_id: currentSessionId ?? null,
      include_in_icf_progress: effectiveIncludeInIcfProgress,
      analysis_mode: effectiveAnalysisMode,
      requested_level: effectiveRequestedLevel,
      include_marker_table: effectiveIncludeMarkerTable,
    });
    trackAuthEvent('chat_message_sent', {
      session_id: currentSessionId ?? null,
      has_attachment: true,
      attachment_type: fileMeta.file_type,
      message_length: analysisDescription.length,
    });

    setShowInstructionsModal(false);
    setAnalyzingFile(true);

    // Show the dedicated AnalysisProgress modal for transcripts and manual
    // session uploads. This provides clear UX feedback for non-technical users.
    if (routeToProgressModal) {
      cancelPendingAnalysisRef.current = false;
      analysisCompletionHandledRef.current = false;
      analysisAutoOpenRef.current = true;
      setAnalyzingDocumentName(pendingFile.name || t('analysis.progress.document'));
      setShowAnalysisProgress(true);
    }

    // Track which session this analysis is for (will be set after API response if new session)
    // For now, set it to current session if exists
    if (currentSessionId) {
      activeOperationSessionRef.current = currentSessionId;
      console.log('🎯 Active operation session set to existing:', currentSessionId);
    }

    // When the AnalysisProgress modal is driving the UX we don't add messages to
    // chat - the modal handles it. Only the inline (tool-driven) path adds a user
    // message and thinking bubble.
    let fileMessage: ChatMessage | null = null;
    if (!routeToProgressModal) {
      const uploadMessageId = Date.now();
      fileMessage = {
        id: uploadMessageId,
        role: 'user',
        content: t('chat.fileUploaded')
          .replace('{{filename}}', pendingFile.name),
        created_at: new Date().toISOString(),
      };
      const thinkingMessage: ChatMessage = {
        id: WAITING_BUBBLE_ID,
        role: 'assistant',
        content: t(waitingTipKey),
        created_at: new Date().toISOString(),
      } as any;
      setMessages(prev => [...prev, fileMessage, thinkingMessage]);
      shouldAutoScrollRef.current = true;
      pendingScrollToBottomRef.current = true;
    }

    if (currentSessionId && fileMessage) {
      addOptimisticMessage(currentSessionId, fileMessage);
    }

    try {
      let asyncResponse: { task_id: string; session_id?: number };

      let retryDispatch: ((key: string) => Promise<{ task_id: string; session_id?: number | null }>) | null = null;

      if (isTranscriptAnalysis) {
        // Use transcript analysis API
        const transcriptDocumentId = pendingFile.documentId;
        const transcriptOptions: AnalysisOptions = {
          analysisMode: effectiveAnalysisMode,
          requestedLevel: effectiveAnalysisMode === 'icf' ? effectiveRequestedLevel : undefined,
          includeMarkerTable: effectiveAnalysisMode === 'icf' ? effectiveIncludeMarkerTable : undefined,
          customPrompt: effectiveAnalysisMode === 'custom' ? effectiveCustomPrompt.trim() : undefined,
          includeInIcfProgress: effectiveIncludeInIcfProgress,
          language: effectiveLanguage,
        };
        retryDispatch = (key: string) =>
          transcriptionAPI.analyzeTranscript(transcriptDocumentId, { ...transcriptOptions, idempotencyKey: key });
        asyncResponse = await retryDispatch(idempotencyKey);
      } else {
        // Use regular file upload API
        const fileData = getAnalysisUploadFile(pendingFile);

        const extraToolResults = pendingToolResultsRef.current || [];
        const uploadToolResult = pendingUploadToolRef.current
          ? {
              tool_call_id: pendingUploadToolRef.current.tool_call_id,
              tool_name: pendingUploadToolRef.current.tool_name,
              status: 'success',
              result: {
                filename: pendingFile.name,
                size: pendingFile.size,
              },
            }
          : null;
        const combinedToolResults = [
          ...extraToolResults,
          ...(uploadToolResult ? [uploadToolResult] : []),
        ];
        const toolResults: ToolResult[] | undefined = combinedToolResults.length > 0
          ? combinedToolResults
          : undefined;
        const forceIntent =
          pendingToolIntentRef.current === 'analyze' || pendingToolIntentRef.current === 'hybrid'
            ? pendingToolIntentRef.current
            : undefined;

        const fileOptions = {
          analysisMode: effectiveAnalysisMode,
          requestedLevel: effectiveAnalysisMode === 'icf' ? effectiveRequestedLevel : undefined,
          includeMarkerTable: effectiveAnalysisMode === 'icf' ? effectiveIncludeMarkerTable : undefined,
          customPrompt: effectiveAnalysisMode === 'custom' ? effectiveCustomPrompt.trim() : undefined,
          includeInIcfProgress: effectiveIncludeInIcfProgress,
          language: effectiveLanguage,
          sessionId: currentSessionId || undefined,
        };
        asyncResponse = await documentsAPI.analyze(fileData, {
          ...fileOptions,
          toolResults,
          forceIntent,
          idempotencyKey,
        });
        // A retry re-sends the file only; the tool results were consumed.
        retryDispatch = (key: string) => documentsAPI.analyze(fileData, { ...fileOptions, idempotencyKey: key });
        pendingUploadToolRef.current = null;
        pendingToolResultsRef.current = [];
        pendingToolIntentRef.current = null;
      }

      if (trialStatus?.active) {
        await refreshTrialStatus();
      }

      pendingTaskId = asyncResponse.task_id;
      if (routeToProgressModal && cancelPendingAnalysisRef.current) {
        cancelledTaskIdsRef.current.add(asyncResponse.task_id);
        try {
          await taskAPI.cancel(asyncResponse.task_id);
        } catch (cancelError) {
          console.warn('Failed to cancel just-created analysis task:', cancelError);
        }
        setShowAnalysisProgress(false);
        setAnalysisTaskId(null);
        setAnalysisSessionId(null);
        setAnalyzingDocumentName(null);
        setAnalyzingFile(false);
        return;
      }

      const pendingTask: PendingTask = {
        taskId: asyncResponse.task_id,
        sessionId: asyncResponse.session_id ?? currentSessionId ?? 0,
        kind: 'analysis',
        createdAt: Date.now(),
        filename: pendingFile?.name,
        instructions: analysisDescription,
      };
      if (pendingTask.sessionId) {
        addPendingTask(pendingTask);
      }
      operationSessionId = asyncResponse.session_id ?? currentSessionId ?? null;

      const wasNewSession = !currentSessionId;

      // ALWAYS set the session ID from the response to ensure continuity
      // This prevents creating new chats when sending followup messages
      if (asyncResponse.session_id) {
        if (fileMessage) {
          addOptimisticMessage(asyncResponse.session_id, fileMessage);
        }
        // Update the active operation session ref so thinking bubble stays in correct session
        activeOperationSessionRef.current = asyncResponse.session_id;
        // The analysis session goes on screen and ends "New chat" mode, so
        // follow-up messages go to this session, not a new one.
        adoptSession(asyncResponse.session_id);
      }

      // When the AnalysisProgress modal is driving the UX it handles WebSocket
      // tracking. We just hand it the task ID and let the modal take over.
      if (routeToProgressModal) {
        analysisRetryRef.current = retryDispatch;
        setAnalysisRetryAvailable(Boolean(retryDispatch));
        setAnalysisTaskId(asyncResponse.task_id);
        if (asyncResponse.session_id) {
          setAnalysisSessionId(asyncResponse.session_id);
        }
        console.log('📊 Analysis started (modal), task:', asyncResponse.task_id);
        // Don't wait inline - the modal handles completion via its onComplete callback
        // Clear analyzing state since modal is now handling UX
        setAnalyzingFile(false);
        return; // Exit early - modal handles the rest
      }

      // Regular file analysis - wait for completion via WebSocket (with polling fallback)
      taskWaiterRef.current.add(asyncResponse.task_id);
      setActiveTaskId(asyncResponse.task_id);
      const taskResult = await waitForTask(asyncResponse.task_id, {
        timeoutMs: 600_000, // 10 minutes for OCR + analysis + persistence
      });

      if (taskResult.status === 'FAILURE') {
        throw new Error(taskResult.error || 'Analysis failed');
      }

      setMessages(prev => prev.filter(m => m.id !== WAITING_BUBBLE_ID));

      if (asyncResponse.session_id) {
        setAnalyzingFile(false);
        // Suppress bubble: state update hasn't rendered yet, so loadMessages
        // would see stale analyzingFile=true and re-add the thinking bubble
        suppressBubbleRef.current = true;
        // Clear optimistic messages — server is now authoritative
        optimisticMessagesRef.current.delete(asyncResponse.session_id);
        await loadMessages(asyncResponse.session_id);
        suppressBubbleRef.current = false;

        if (wasNewSession) {
          await loadSessions();
        }

        await loadQuotaStatus();
      }

      trackAuthEvent('document_analysis_completed', {
        ...fileMeta,
        session_id: asyncResponse.session_id ?? currentSessionId ?? null,
        include_in_icf_progress: effectiveIncludeInIcfProgress,
        was_new_session: wasNewSession,
        duration_ms: analysisStartRef.current ? Date.now() - analysisStartRef.current : undefined,
      });
      void handleAnalysisMilestone('document_analysis_completed');

      // Mark this session as having analysis (for tip tracking + indicator)
      markSessionAnalyzed(operationSessionId);
      messageCountRef.current = 0;

      // Refresh credit status after consuming a credit
      refreshStatus();
      // Clear the viewing transcript after successful analysis
      setViewingTranscript(null);

    } catch (error: any) {
      setMessages(prev => prev.filter(m => m.id !== WAITING_BUBBLE_ID));
      if (operationSessionId) {
        optimisticMessagesRef.current.delete(operationSessionId);
      }
      const errorStatus = errorStatusOf(error);
      const errorType = errorStatus ? `http_${errorStatus}` : (error?.message ? 'exception' : 'unknown');
      trackAuthEvent('document_analysis_failed', {
        ...fileMeta,
        session_id: currentSessionId ?? null,
        include_in_icf_progress: effectiveIncludeInIcfProgress,
        error_type: errorType,
        duration_ms: analysisStartRef.current ? Date.now() - analysisStartRef.current : undefined,
      });
      if (routeToProgressModal) {
        // Hide the progress modal and show error state
        setShowAnalysisProgress(false);
        setAnalysisTaskId(null);
        setAnalysisSessionId(null);
        setAnalyzingDocumentName(null);
      }
      const trialUsed = trialAnalysisUsedMessage(t, language, error);
      showDialog(trialUsed || `${t('chat.analysisError')}: ${chatSendErrorMessage(t, error, 'chat.analysisFailed')}`);
    } finally {
      if (pendingTaskId) {
        removePendingTask(pendingTaskId);
        taskWaiterRef.current.delete(pendingTaskId);
      }
      setActiveTaskId(null);
      setAnalyzingFile(false);
      setPendingFile(null);
      setAnalysisMode('icf');
      setRequestedLevel('auto');
      setIncludeMarkerTable(false);
      setCustomPrompt('');
      setIncludeInICFProgress(true);
      activeOperationSessionRef.current = null;
      suppressBubbleRef.current = false;
      analysisStartRef.current = null;
      // The dispatch is done (the modal tracks the job from here); the next
      // submit is a new intent. "Try again" uses its own fresh key.
      analysisIntent.reset();
      isAnalyzingInProgressRef.current = false;
      console.log('🔓 Analysis ref guard cleared, active operation session cleared');
    }
  };

  const cancelAnalysis = () => {
    trackAuthEvent('document_analysis_cancelled', {
      has_file: Boolean(pendingFile),
    });
    setShowInstructionsModal(false);
    setPendingFile(null);
    setAnalysisMode('icf');
    setRequestedLevel('auto');
    setIncludeMarkerTable(false);
    setCustomPrompt('');
    setAnalysisLanguage(language === 'en' ? 'en' : 'tr');
    setIncludeInICFProgress(true);
  };

  // Handle analyze from transcript viewer - open analysis modal with document ID
  const handleAnalyzeTranscriptWithModal = (documentId: number) => {
    // Close the transcript viewer first
    setShowTranscriptViewer(false);

    // Set up the pending file as a virtual transcript reference
    setPendingFile({
      uri: `transcript://${documentId}`,
      name: viewingTranscript?.original_filename || `transcript_${documentId}`,
      mimeType: 'text/transcript',
      isTranscript: true,
      documentId: documentId,
    });
    setAnalysisMode('icf');
    setRequestedLevel('auto');
    setIncludeMarkerTable(false);
    setCustomPrompt('');
    setAnalysisLanguage(language === 'en' ? 'en' : 'tr');
    setShowInstructionsModal(true);
  };

  // =================== ANALYSIS PROGRESS HANDLERS ===================

  const handleAnalysisProgressClose = () => {
    analysisIntent.reset();
    analysisRetryRef.current = null;
    setAnalysisRetryAvailable(false);
    setShowAnalysisProgress(false);
    setAnalysisTaskId(null);
    setAnalysisSessionId(null);
    setAnalyzingDocumentName(null);
  };

  const handleAnalysisProgressCancel = (taskId?: string | null) => {
    if (taskId) {
      cancelledTaskIdsRef.current.add(taskId);
      removePendingTask(taskId);
      taskWaiterRef.current.delete(taskId);
    } else {
      cancelPendingAnalysisRef.current = true;
    }
    analysisIntent.reset();
    analysisRetryRef.current = null;
    setAnalysisRetryAvailable(false);
    setShowAnalysisProgress(false);
    setAnalysisTaskId(null);
    setAnalysisSessionId(null);
    setAnalyzingDocumentName(null);
    setAnalyzingFile(false);
    setActiveTaskId(null);
  };

  const handleViewAnalysisResult = async (sessionId: number) => {
    // Navigate to the chat session with the analysis results
    showSession(sessionId);
    await loadMessages(sessionId);
    await loadSessions();
    handleAnalysisProgressClose();
  };

  const handleAnalysisComplete = async (sessionId: number) => {
    // The modal's WebSocket and polling completion paths can both fire for the
    // same task — only handle the first.
    if (analysisCompletionHandledRef.current) return;
    analysisCompletionHandledRef.current = true;
    analysisIntent.reset();

    console.log('📊 Analysis completed, session:', sessionId);
    trackAuthEvent('document_analysis_completed', {
      session_id: sessionId,
      source: 'transcript_analysis',
    });
    void handleAnalysisMilestone('transcript_analysis');
    markSessionAnalyzed(sessionId);

    if (!analysisAutoOpenRef.current) {
      // Audio flow handles its own navigation/close inline — just refresh.
      await Promise.all([loadQuotaStatus(), loadRecentDocuments(), refreshTrialStatus()]);
      refreshStatus();
      return;
    }

    // Auto-open the result: navigate to the analyzed session and load its
    // messages, then dismiss the progress modal. Avoids the extra "View
    // Analysis" tap — the report appears directly in the chat.
    showSession(sessionId);
    await loadMessages(sessionId);
    await Promise.all([loadSessions(), loadQuotaStatus(), loadRecentDocuments(), refreshTrialStatus()]);
    refreshStatus();
    handleAnalysisProgressClose();
  };

  const handleAnalysisError = (error: string) => {
    console.error('📊 Analysis error:', error);
    // Terminal: the failed job's key must not be reused.
    analysisIntent.reset();
    trackAuthEvent('document_analysis_failed', {
      error_type: 'websocket_error',
    });
  };

  // "Try again" on a failed upload-driven analysis: same input, NEW
  // Idempotency-Key (the old key would replay the failed job).
  const handleAnalysisRetry = async () => {
    const dispatch = analysisRetryRef.current;
    if (!dispatch || analysisRetryInFlightRef.current) return;
    analysisRetryInFlightRef.current = true;
    cancelPendingAnalysisRef.current = false;
    analysisCompletionHandledRef.current = false;
    analysisAutoOpenRef.current = true;
    setAnalysisTaskId(null);
    setAnalysisSessionId(null);
    try {
      const response = await dispatch(analysisIntent.renew());
      if (cancelPendingAnalysisRef.current) {
        cancelledTaskIdsRef.current.add(response.task_id);
        try {
          await taskAPI.cancel(response.task_id);
        } catch (cancelError) {
          console.warn('Failed to cancel just-created retry analysis task:', cancelError);
        }
        return;
      }
      if (response.session_id) {
        // Same continuity as the first dispatch: follow-ups land in this session.
        adoptSession(response.session_id);
        setAnalysisSessionId(response.session_id);
      }
      setAnalysisTaskId(response.task_id);
    } catch (error: any) {
      handleAnalysisProgressClose();
      // CHAT-19: never show raw backend/network text; same mapping as the first dispatch.
      const trialUsed = trialAnalysisUsedMessage(t, language, error);
      showDialog(trialUsed || `${t('chat.analysisError')}: ${chatSendErrorMessage(t, error, 'chat.analysisFailed')}`);
    } finally {
      analysisRetryInFlightRef.current = false;
    }
  };

  // Submit handler for the shared <AnalysisModal>. Persists the chosen options
  // into local state (so the post-submit `finally` block's resets behave the
  // same as before) and forwards them as overrides to the analysis flow so
  // they're not subject to setState batching.
  // NOTE: deliberately a plain function (not useCallback). It calls
  // analyzeWithInstructions, which closes over the latest `pendingFile`. A
  // memoized version with an incomplete dependency array captured a stale
  // analyzeWithInstructions where pendingFile was still null, so "Start
  // analysis" silently bailed at the `if (!pendingFile) return` guard.
  const handleAnalysisModalSubmit = async (options: AnalysisOptions) => {
      // Synchronous guard against double-fire (AnalysisModal already disables
      // its submit button while loading, but defensive in case of races).
      if (isAnalyzingInProgressRef.current) {
        if (!analyzingFile && !analysisTaskId && !activeTaskId) {
          console.warn('⚠️ Stale analysis in-progress guard detected, resetting');
          isAnalyzingInProgressRef.current = false;
        } else {
          console.log('🚫 handleAnalysisModalSubmit blocked - analysis already in progress');
          return;
        }
      }

      trackAuthEvent('document_analysis_cta_clicked', {
        analysis_mode: options.analysisMode,
      });

      const normalizedLanguage: 'tr' | 'en' =
        options.language === 'en' || options.language === 'tr'
          ? options.language
          : analysisLanguage;
      setAnalysisMode(options.analysisMode);
      setRequestedLevel(options.requestedLevel ?? 'auto');
      setIncludeMarkerTable(options.includeMarkerTable ?? false);
      setCustomPrompt(options.customPrompt ?? '');
      setAnalysisLanguage(normalizedLanguage);
      setIncludeInICFProgress(options.includeInIcfProgress);
      trackAuthEvent('analysis_icf_toggle', {
        enabled: options.includeInIcfProgress,
        default_state: true,
      });
      await analyzeWithInstructions(options);
  };

  return {
    // Analysis options modal
    showInstructionsModal,
    pendingFile,
    pickDocumentForAnalysis,
    cancelAnalysis,
    handleAnalyzeTranscriptWithModal,
    handleAnalysisModalSubmit,
    // AnalysisProgress modal (the audio flow drives it too)
    showAnalysisProgress,
    setShowAnalysisProgress,
    analysisTaskId,
    setAnalysisTaskId,
    analysisSessionId,
    setAnalysisSessionId,
    analyzingDocumentName,
    setAnalyzingDocumentName,
    analysisRetryAvailable,
    setAnalysisRetryAvailable,
    analysisRetryRef,
    analysisAutoOpenRef,
    analysisCompletionHandledRef,
    handleAnalysisProgressClose,
    handleAnalysisProgressCancel,
    handleViewAnalysisResult,
    handleAnalysisComplete,
    handleAnalysisError,
    handleAnalysisRetry,
  };
}
